/**
 * Internal provenance inspector (canonical spec §VIII): only with `?debug=1`, never by default.
 * Per chapter: source event IDs and the producer's editorial decisions; per show: model, latency,
 * attempts, voice and validation. Reads `GET /director/:id/inspector` when the server has it.
 */
import { useEffect, useRef, useState } from 'react';
import type { DirectorResponse } from '@clutch/core';
import type { DirectorInspection } from './api';

export function debugEnabled(search = typeof location !== 'undefined' ? location.search : ''): boolean {
  return new URLSearchParams(search).get('debug') === '1';
}

export type InspectorRow = { segmentId: string; heading?: string; eventIds: string[]; decision: string[] };

/** Pure: what the inspector lists per chapter (unit-tested). */
export function inspectorRows(d: DirectorResponse, remote?: DirectorInspection): InspectorRow[] {
  return d.segments.map((s) => {
    const ch = d.showPlan?.chapters.find((c) => c.segmentId === s.segmentId);
    const r = remote?.chapters?.find((c) => c.segmentId === s.segmentId);
    const reasons = d.editorial?.selected.find((x) => x.momentId === s.momentId)?.reasonKeys ?? [];
    const cites = d.narration?.citations?.find((c) => c.segmentId === s.segmentId);
    const decision = [
      ch ? `${ch.sceneRole} · ${ch.emphasis}` : '',
      ch?.commentaryIntent ?? '',
      ch?.visualCues.length ? `cues: ${ch.visualCues.map((c) => c.type).join(' → ')}` : '',
      ch?.personalizationWhy ? `why: ${ch.personalizationWhy}` : '',
      reasons.length ? `reasons: ${reasons.join(', ')}` : '',
      cites?.factIds.length ? `facts: ${cites.factIds.join(', ')}` : '',
      r?.decision ?? '',
    ].filter(Boolean);
    return { segmentId: s.segmentId, ...(ch?.heading ? { heading: ch.heading } : {}), eventIds: r?.eventIds ?? s.eventIds, decision };
  });
}

export function DebugInspector({ director: d, load, voice }: { director: DirectorResponse; load?: (id: string) => Promise<DirectorInspection | undefined>; voice?: string }) {
  // Note: `load` is bound to the owner fan by the caller (the server inspector is owner-only).
  const [open, setOpen] = useState(true);
  const [remote, setRemote] = useState<DirectorInspection>();
  const loader = useRef(load);
  loader.current = load;
  useEffect(() => {
    let alive = true;
    setRemote(undefined);
    void loader.current?.(d.directorId).then((r) => { if (alive) setRemote(r); }).catch(() => undefined);
    return () => { alive = false; };
  }, [d.directorId]);
  const i = { ...(d.inspector ?? {}), ...(remote ?? {}) } as DirectorInspection;
  const validation = i.validation ?? d.showPlan?.factualValidation.errors ?? [];
  const facts: [string, string | undefined][] = [
    ['model', i.model ?? d.generation.model ?? d.narration?.model],
    ['provider', i.provider ?? d.generation.provider],
    ['prompt', i.promptVersion],
    ['latency', i.latencyMs !== undefined ? `${i.latencyMs} ms` : undefined],
    ['attempts', i.attempts !== undefined ? String(i.attempts) : undefined],
    ['voice', [i.voice ?? d.show?.voiceName ?? voice, i.voiceId ?? d.show?.voiceId].filter(Boolean).join(' · ') || undefined],
    ['synthesis', i.synthesisMs !== undefined ? `${i.synthesisMs} ms` : undefined],
    ['cache', i.cache !== undefined ? String(i.cache) : String(d.generation.cacheHit)],
    ['cutoff', `seq ${d.asOf.seq} · ${d.asOf.status}`],
    ['evidence', d.provenance.evidenceHash.slice(0, 16)],
    ['validation', d.showPlan ? (d.showPlan.factualValidation.valid ? 'valid' : 'INVALID') : undefined],
    ['requests', i.requestIds?.join(', ')],
  ];
  if (!open) return <button className="dbg__pill" onClick={() => setOpen(true)} data-testid="debug-open">debug</button>;
  return (
    <aside className="dbg" data-testid="debug-inspector" aria-label="Director inspector">
      <header><b>Director inspector</b><span>{d.directorId}</span><button onClick={() => setOpen(false)} aria-label="Close inspector">×</button></header>
      <dl className="dbg__facts">{facts.filter(([, v]) => v).map(([k, v]) => <div key={k}><dt>{k}</dt><dd data-testid={`debug-${k}`}>{v}</dd></div>)}</dl>
      {validation.length > 0 && <ul className="dbg__val">{validation.map((v) => <li key={v}>{v}</li>)}</ul>}
      <ol className="dbg__chapters">
        {inspectorRows(d, remote).map((r) => (
          <li key={r.segmentId} data-testid="debug-chapter">
            <b>{r.heading ?? r.segmentId}</b><small>{r.segmentId}</small>
            <code>{r.eventIds.length ? r.eventIds.join(' ') : '—'}</code>
            {r.decision.map((x) => <span key={x}>{x}</span>)}
          </li>
        ))}
      </ol>
    </aside>
  );
}
